import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';

interface GeneralRoiTrophiesCountUpNumberSignature {
  Args: {
    currency?: string;
    duration?: number;
    prefix?: string;
    suffix?: string;
    target: number;
  };
  Element: HTMLSpanElement;
}

function easeOutExpo(t: number): number {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

export default class GeneralRoiTrophiesCountUpNumber extends Component<GeneralRoiTrophiesCountUpNumberSignature> {
  @service declare intl: Services['intl'];

  @tracked currentValue = 0;

  private animationFrameId: number | null = null;

  // Intentionally runs once on initial render only — does not re-animate when @target changes.
  startOnRender = modifier(() => {
    if (this.args.target === 0) {
      return;
    }

    this.startAnimation();

    return (): void => {
      this.cancelAnimation();
    };
  });

  get duration(): number {
    return this.args.duration ?? 1500;
  }

  get formattedValue(): string {
    if (this.args.currency) {
      return this.intl.formatNumber(this.currentValue, {
        style: 'currency',
        currency: this.args.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        // ROI values should always show their sign (+/-) to indicate gain or loss.
        signDisplay: 'always',
      });
    }

    return this.intl.formatNumber(this.currentValue, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  willDestroy(): void {
    super.willDestroy();
    this.cancelAnimation();
  }

  private cancelAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private startAnimation(): void {
    if (this.duration <= 0) {
      this.currentValue = this.args.target;
      return;
    }

    const startTime = performance.now();
    const { target } = this.args;
    // Decimal support: hardcoded to 0 decimals for now. To support N decimals,
    // replace 0 with a `decimals` arg (e.g. Math.pow(10, decimals)).
    const multiplier = Math.pow(10, 0);

    const animate = (now: number): void => {
      const elapsed = now - startTime;
      const progress = easeOutExpo(Math.min(elapsed / this.duration, 1));

      this.currentValue =
        Math.round(progress * target * multiplier) / multiplier;

      if (elapsed < this.duration) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.currentValue = target;
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  <template>
    <span data-test-id="count-up-number" {{this.startOnRender}} ...attributes>
      {{#if @prefix}}
        {{@prefix}}
      {{/if}}
      {{this.formattedValue}}
      {{#if @suffix}}
        {{@suffix}}
      {{/if}}
    </span>
  </template>
}
